import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { FORM_ERROR } from 'final-form';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showNotification, showError } from '@vk-hr-tek/core/notifications';

import {
  PolicyService,
  CreatePolicyCategoryRequest,
  PolicyCategoryAccountOptionsResponse,
  GetPolicyCategoryListResponse,
  GetPolicyCategoryListQuery,
  CompanyPolicyListItem as CompanyPolicy,
  EmployeePolicyListItem as EmployeePolicy,
} from '@app/gen/policy';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import { PolicyState } from '../policy.state';

const resetCategoriesCreationData = createAction(
  'policy/resetCategoriesCreationData',
);

export const getCategoryAccountOptions = createAsyncThunk<
  PolicyCategoryAccountOptionsResponse,
  void,
  ThunkExtra
>(
  'policy/getCategoryAccountOptions',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(PolicyService);
      return await service.getCategoryAccountOptions();
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCategoryByAccountId = createAsyncThunk<
  GetPolicyCategoryListResponse | undefined,
  { account_ids: string[] },
  ThunkExtra
>(
  'policy/getCategoryByAccountId',
  async (query, { rejectWithValue, getState, extra: { inject } }) => {
    try {
      const service = inject(PolicyService);

      const categoriesByAccountId =
        getState().policy.categories.creationData.categoriesByAccountId;

      if (categoriesByAccountId[query.account_ids[0]]) return;

      return await service.getPolicyCategoryList({
        query: {
          ...query,
          hierarchic_view: true,
        },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getPolicyCategoryList = createAsyncThunk<
  GetPolicyCategoryListResponse,
  GetPolicyCategoryListQuery | void,
  ThunkExtra
>(
  'policy/getPolicyCategoryList',
  async (query, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(PolicyService);
      return await service.getPolicyCategoryList({
        query: query || {},
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const createPolicyCategory = createAsyncThunk<
  void,
  ThunkValuesAndActions<CreatePolicyCategoryRequest>,
  ThunkExtra
>(
  'policy/createPolicyCategory',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyService);
      const response = await service.createPolicyCategory({ body: values });

      actions.resolve(response?.id);
      dispatch(showNotification('Категория создана'));
      dispatch(resetCategoriesCreationData());
      await dispatch(
        getPolicyCategoryList({ offset: 0, limit: 20, hierarchic_view: true }),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось создать категорию'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deletePolicyCategory = createAsyncThunk<
  void,
  ThunkValuesAndActions<string>,
  ThunkExtra
>(
  'policy/deletePolicyCategory',
  async (
    { values: categoryId, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyService);
      await service.deletePolicyCategory({
        params: { category_id: categoryId },
      });

      actions.resolve(null);
      dispatch(showNotification('Категория удалена'));
      dispatch(getPolicyCategoryList({ offset: 0, limit: 20 }));
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось удалить категорию'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const categoriesReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<CompanyPolicy | EmployeePolicy, string> & PolicyState
  >,
) => {
  builder.addCase(getCategoryAccountOptions.pending, (state) => {
    state.categories.creationData.accountsStatus = 'loading';
  });
  builder.addCase(getCategoryAccountOptions.fulfilled, (state, { payload }) => {
    state.categories.creationData.accountsStatus = 'complete';
    state.categories.creationData.accounts = payload;
  });
  builder.addCase(getCategoryAccountOptions.rejected, (state) => {
    state.categories.creationData.accountsStatus = 'failed';
  });

  builder.addCase(getCategoryByAccountId.pending, (state) => {
    state.categories.creationData.categoriesStatus = 'loading';
  });
  builder.addCase(
    getCategoryByAccountId.fulfilled,
    (state, { payload, meta: { arg } }) => {
      state.categories.creationData.categoriesStatus = 'complete';
      if (payload) {
        state.categories.creationData.categoriesByAccountId[
          arg.account_ids[0]
        ] = payload.categories;
      }
    },
  );

  builder.addCase(resetCategoriesCreationData, (state) => {
    state.categories.creationData.accountsStatus = 'idle';
    state.categories.creationData.accounts = [];
    state.categories.creationData.categoriesStatus = 'idle';
    state.categories.creationData.categoriesByAccountId = {};
  });

  builder.addCase(getPolicyCategoryList.pending, (state) => {
    state.categories.status = 'loading';
  });
  builder.addCase(getPolicyCategoryList.fulfilled, (state, { payload }) => {
    state.categories.status = 'complete';
    state.categories.items = payload.categories;
    state.categories.total = payload.total;
  });
  builder.addCase(
    getPolicyCategoryList.rejected,
    (state, { payload, error }) => {
      state.categories.status = 'failed';
      state.categories.error =
        payload ||
        ({
          info: (error && error.message) || 'Что-то пошло не так',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(deletePolicyCategory.pending, (state) => {
    state.categories.deleteStatus = 'loading';
  });
  builder.addCase(deletePolicyCategory.fulfilled, (state) => {
    state.categories.deleteStatus = 'complete';
  });
  builder.addCase(deletePolicyCategory.rejected, (state) => {
    state.categories.deleteStatus = 'failed';
  });
};
